import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out, object = logged in
  const [character, setCharacter] = useState(null);
  const [ready, setReady] = useState(false); // true once initial auth + character load settles

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return false;
    }
  }, []);

  const loadCharacter = useCallback(async () => {
    try {
      const { data } = await api.get("/character");
      setCharacter(data.character);
      return data.character;
    } catch {
      setCharacter(null);
      return null;
    }
  }, []);

  useEffect(() => {
    // Returning from Google OAuth: let AuthCallback exchange the session_id first.
    if (window.location.hash && window.location.hash.includes("session_id=")) {
      setReady(true);
      return;
    }
    (async () => {
      try {
        const u = await refreshMe();
        if (u) await loadCharacter();
      } finally {
        setReady(true);
      }
    })();
  }, [refreshMe, loadCharacter]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    await loadCharacter();
    return data;
  };

  const register = async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    setUser(data);
    setCharacter(null);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { if (process.env.NODE_ENV === "development") console.debug("logout request failed", e); }
    setUser(false);
    setCharacter(null);
  };

  return (
    <AuthCtx.Provider value={{ user, setUser, character, setCharacter, ready, login, register, logout, loadCharacter, refreshMe }}>
      {children}
    </AuthCtx.Provider>
  );
}
